import { ChangeDetectionStrategy, Component, DestroyRef, inject, input, output, signal } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { timer } from 'rxjs';

export type AlertType = 'primary' | 'secondary' | 'success' | 'danger' | 'warning' | 'info' | 'light' | 'dark';

/** Matches the fade-out of the theme's `.fade` transition (opacity, 0.15s linear). */
export const ALERT_FADE_MS = 150;

/**
 * Contextual alert box (replaces ng-bootstrap's `ngb-alert`).
 *
 * The `alert`, `alert-<type>`, `alert-dismissible` and `close` classes are the hooks for `styles/components/alert.css`, which owns the colours, padding and radius. The component supplies the box
 * model that the Bootstrap base layer used to provide.
 */
@Component({
  selector: 'app-alert',
  template: `
    <ng-content />
    @if (dismissible()) {
      <button
        type="button"
        class="close absolute right-0 z-2 text-inherit [text-decoration:inherit]"
        aria-label="Close"
        (click)="close()"
      >
        <span aria-hidden="true">&times;</span>
      </button>
    }
  `,
  // Not Tailwind utilities: consumers (`flex`) and the theme (`.card-body .alert:last-child` drops the margin)
  // override these, and `important` utilities would win over both.
  styles: `
    :host {
      display: block;
      position: relative;
      margin-bottom: 1rem;
    }
  `,
  host: {
    role: 'alert',
    class: 'border border-solid transition-opacity duration-150 ease-linear ' + 'motion-reduce:transition-none',
    '[class]': '"alert alert-" + type()',
    '[class.alert-dismissible]': 'dismissible()',
    '[class.opacity-0]': 'closing()',
  },
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class AlertComponent {
  #destroyRef = inject(DestroyRef);

  public type = input<AlertType>('warning');
  public dismissible = input(false);
  /** Emitted once the alert has faded out after the close button was clicked. */
  public closed = output<void>();

  protected closing = signal(false);

  public close(): void {
    this.closing.set(true);
    timer(ALERT_FADE_MS)
      .pipe(takeUntilDestroyed(this.#destroyRef))
      .subscribe(() => this.closed.emit());
  }
}
